import { PASSING_GRADE } from '../../../shared/constant/grading';
import { CURRENT_SCHOOL_YEAR } from '../../../shared/constant/schoolYears';
import { TERMS } from '../../../shared/constant/terms';
import { getSubjectGrades } from '../services/gradesService';

// The school's E-Class Summary form (public/templates), filled in for one section. The template keeps
// the logos, school details, borders and print setup; only the cells below are written.
const TEMPLATE_URL = `${process.env.PUBLIC_URL}/templates/e-class-summary-template.xlsx`;

const CELLS = {
  schoolYear: 'V6',
  gradeSection: 'K9',
  subject: 'T9',
  teacher: 'K10',
  subjectType: 'T10',
};
// Each list has 35 numbered rows; columns are the left cell of each merged block.
const LISTS = { M: { firstRow: 13, label: 'male' }, F: { firstRow: 49, label: 'female' } };
const ROWS_PER_LIST = 35;
const COLUMNS = { name: 'B', terms: ['F', 'K', 'P'], average: 'T', remarks: 'W' };

export const fullName = (student) =>
  `${student.lastname}, ${student.firstname}${student.middlename ? ` ${student.middlename}` : ''}`.toUpperCase();

// "ARIEL A. GERONA", as the template has it.
export function teacherName(teacher) {
  if (!teacher) return '';
  const initial = teacher.middlename ? ` ${teacher.middlename.trim().charAt(0)}.` : '';
  return `${teacher.firstname}${initial} ${teacher.lastname}`.toUpperCase();
}

// The final grade is the three term grades averaged and rounded, once all three are in.
function finalGrade(termGrades) {
  if (termGrades.some((g) => g == null)) return null;
  return Math.round(termGrades.reduce((sum, g) => sum + g, 0) / termGrades.length);
}

export function downloadBuffer(buffer, fileName) {
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

// Downloads the filled-in summary for one section's `students`. Resolves to the students left out
// because they have no sex recorded (the form only has Male and Female lists).
export async function exportGradeSummary({ subject, section, students }) {
  const [{ default: ExcelJS }, response, ...termRows] = await Promise.all([
    import('exceljs'),
    fetch(TEMPLATE_URL),
    ...TERMS.map((term) => getSubjectGrades(subject.id, term)),
  ]);
  if (!response.ok) throw new Error('Could not load the E-Class Summary template.');

  const gradesByTerm = termRows.map((rows) => Object.fromEntries(rows.map((r) => [r.student_id, r.term_grade])));
  const byName = (a, b) => a.lastname.localeCompare(b.lastname) || a.firstname.localeCompare(b.firstname);
  const lists = {
    M: students.filter((s) => s.gender === 'M').sort(byName),
    F: students.filter((s) => s.gender === 'F').sort(byName),
  };
  Object.entries(lists).forEach(([gender, list]) => {
    if (list.length > ROWS_PER_LIST) {
      throw new Error(
        `The form has room for ${ROWS_PER_LIST} ${LISTS[gender].label} students, but ${section} has ${list.length}.`
      );
    }
  });

  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(await response.arrayBuffer());
  const sheet = workbook.worksheets[0];

  sheet.getCell(CELLS.schoolYear).value = subject.school_year || CURRENT_SCHOOL_YEAR;
  sheet.getCell(CELLS.gradeSection).value = `${subject.grade_level} - ${section}`;
  sheet.getCell(CELLS.subject).value = subject.name;
  sheet.getCell(CELLS.teacher).value = teacherName(subject.teachers);
  sheet.getCell(CELLS.subjectType).value = subject.category;

  Object.entries(lists).forEach(([gender, list]) => {
    list.forEach((student, i) => {
      const row = LISTS[gender].firstRow + i;
      const termGrades = gradesByTerm.map((grades) => grades[student.id] ?? null);
      const final = finalGrade(termGrades);

      sheet.getCell(`${COLUMNS.name}${row}`).value = fullName(student);
      COLUMNS.terms.forEach((col, t) => {
        sheet.getCell(`${col}${row}`).value = termGrades[t];
      });
      sheet.getCell(`${COLUMNS.average}${row}`).value = final;
      sheet.getCell(`${COLUMNS.remarks}${row}`).value =
        final == null ? null : final >= PASSING_GRADE ? 'PASSED' : 'FAILED';
    });
  });

  const safe = (text) => String(text).replace(/[\\/:*?"<>|]+/g, '').trim();
  downloadBuffer(
    await workbook.xlsx.writeBuffer(),
    `E-Class Summary - ${safe(subject.name)} - ${safe(`${subject.grade_level}-${section}`)}.xlsx`
  );

  return students.filter((s) => s.gender !== 'M' && s.gender !== 'F');
}
